import DropDown from "components/basic/autoComplete";
import Typography from "components/basic/typography";
import { useCallback, useEffect, useRef, useState } from "react";
import FieldContainer from "./component/fieldContainer";
import TextField from "components/basic/textField";
import Slider from "components/basic/slider";
import LineChart from "components/basic/lineChart";
import { provinces, ProvinceType } from "./data";
import {
  useEvListQuery,
  useIceListQuery,
  usePhevListQuery,
} from "generated/graphql";
import CalculatorFormulas, {
  CalculateDataProps,
  CalculationData,
} from "./component/calculatorFormulas";
import LoadingSpinner from "components/basic/loadingSpinner";
import GhgInfographic from "./component/ghgInfographic";
import { useTheme } from "@mui/material";
import FuelCostEmissionsChart from "pages/calculator/component/fuelCostEmissionsChart";
import { RangeBarChart } from "./component/rangeBarChart";
import NetSavingsChart from "./component/netSavingGraph";
import AdvanceFeature, {
  AdvanceFeatureData,
} from "./component/advanceFeaturesContainer";
import { useTranslation } from "react-i18next";
import { track } from "analytics";

interface CalculatorProps {}

interface CalculatorData {
  lifespan: number;
  fuelPrice: number;
  utilityKWhRate: number;
  annualDrivingDistance: number;
  vehicleType?: "EV" | "PHEV";
  ev?: { id: number; label: string };
  ice?: { id: number; label: string };
  province?: ProvinceType;
}

const defaultCalculationData: CalculationData = {
  isSet: false,
  annualFuelCostSaving: 0,
  lifetimeFuelCostSaving: 0,
  evAnnualFuelCostSavings: 0,
  iceAnnualFuelCostSavings: 0,
  evChartData: [0],
  iceChartData: [0],
  ghgData: {
    evCarbonFootprint: 0,
    evComparisonText: [""],
    iceCarbonFootprint: 0,
    iceComparisonText: [""],
  },
  evLifetimeFuelCost: [0],
  iceLifetimeFuelCost: [0],
};

const Calculator: React.FC<CalculatorProps> = () => {
  const theme = useTheme();
  const { t } = useTranslation("calculator");

  const [{ data: iceRequest, fetching: iceLoading }] = useIceListQuery();
  const [{ data: phevRequest, fetching: phevLoading }] = usePhevListQuery();
  const [{ data: evRequest, fetching: evLoading }] = useEvListQuery();

  const [formData, setFormData] = useState<CalculatorData>({
    vehicleType: "EV",
    province: { id: 1, code: "ON", name: "Ontario" },
    fuelPrice: 1.75,
    annualDrivingDistance: 15000,
    utilityKWhRate: 0.142,
    lifespan: 10,
  });

  // Data for the graphs
  const [calculationData, setCalculationData] = useState<CalculationData>(
    defaultCalculationData,
  );

  // Default form data from advanced feature calculations
  const [advanceFeatureData, setAdvanceFeatureData] =
    useState<AdvanceFeatureData>({
      includeCostOfVehicle: false,
      includeLease: false,
      includeInsurance: false,
      iceFinance: {
        vehicleCost: 35000,
        interest: 5.5,
        downPayment: 5000,
        paymentDuration: 48,
        insurance: 300,
      },
      evFinance: {
        vehicleCost: 35000,
        interest: 5.5,
        downPayment: 5000,
        paymentDuration: 48,
        insurance: 300,
      },
    });

  const calculatorRunTracked = useRef(false);

  useEffect(() => {
    //data is not yet set, set default data and return
    if (!formData?.ev || !formData?.ice) {
      setCalculationData(() => defaultCalculationData);

      return;
    }

    //set data needed for calculations for the graphs
    const selectedIceEnergyConsumption = iceRequest?.getICEList?.find(
      (item) => {
        return item.id === formData.ice?.id;
      },
    )?.combinedFuelConsumption;

    const selectedIceCO2Emissions = iceRequest?.getICEList?.find((item) => {
      return item.id === formData.ice?.id;
    })?.co2Emissions;

    const selectedEvEnergyConsumption = evRequest?.getEVList?.find((item) => {
      return item.id === formData.ev?.id;
    })?.combinedEnergyConsumption;

    const data: CalculateDataProps = {
      annualDrivingDistance: formData.annualDrivingDistance,
      combinedEnergyConsumption: selectedEvEnergyConsumption
        ? selectedEvEnergyConsumption
        : 0,
      combinedFuelConsumption: selectedIceEnergyConsumption
        ? selectedIceEnergyConsumption
        : 0,
      utilityKWhRate: formData.utilityKWhRate,
      fuelPrice: formData.fuelPrice,
      lifespan: formData.lifespan,
      advanceFeatureData: advanceFeatureData,
      selectedIceCO2Emissions: selectedIceCO2Emissions
        ? selectedIceCO2Emissions
        : 0,
    };

    //calculate data for the graphs
    const newCalculationData = CalculatorFormulas.calculateData(data);
    setCalculationData(() => newCalculationData);

    // Once per visit, at the point the comparison first becomes real. This
    // effect also reruns on every slider nudge and keystroke, which is not a
    // separate use of the tool.
    if (!calculatorRunTracked.current) {
      calculatorRunTracked.current = true;
      track("calculator_run", {
        ev: formData.ev?.label ?? "",
        ice: formData.ice?.label ?? "",
      });
    }
  }, [formData, advanceFeatureData, iceRequest, evRequest]);

  const handleFieldChange = useCallback((name: string, value: string) => {
    switch (name) {
      case "fuelPrice":
      case "lifespan":
      case "utilityKWhRate":
        const parsedValue = parseFloat(value);

        if (isNaN(parsedValue)) {
          return;
        }

        if (parsedValue < 0) {
          return;
        }

        setFormData((prev) => ({
          ...prev,
          [name]: parsedValue,
        }));

        return;

      case "annualDrivingDistance": {
        const parsedValue = parseFloat(value);

        if (isNaN(parsedValue)) {
          return;
        }

        if (parsedValue > 100000 || parsedValue < 1000) {
          return;
        }

        setFormData((prev) => ({
          ...prev,
          annualDrivingDistance: parsedValue,
        }));

        return;
      }
      default:
        return;
    }
  }, []);

  if (iceLoading || phevLoading || evLoading) {
    return (
      <div
        style={{
          height: "500px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  const iceList = (iceRequest?.getICEList ?? []).map((item) => ({
    id: item.id,
    label: `${item.modelYear} ${item.make} ${item.model}`,
  }));

  const evList = (evRequest?.getEVList ?? []).map((item) => ({
    id: item.id,
    label: `${item.modelYear} ${item.make} ${item.model}`,
  }));

  const phevList = (phevRequest?.getPHEVList ?? []).map((item) => ({
    id: item.id,
    label: `${item.modelYear} ${item.make} ${item.model}`,
  }));

  return (
    <div
      style={{
        display: "flex",
        gap: theme.spacing(3),
        width: "100%",
        justifyContent: "center",
      }}
    >
      {/*  row 1 */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 11,
          minWidth: "0",
        }}
      >
        <FieldContainer title={t("ev_or_phev_make")}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(2),
            }}
          >
            <DropDown
              name="ev"
              value={(() => {
                if (!formData?.vehicleType) {
                  return null;
                }

                return formData.vehicleType === "PHEV" ? 1 : 2;
              })()}
              options={[
                { id: 1, label: "PHEV" },
                { id: 2, label: "EV" },
              ]}
              label={t("vehicle_type")}
              onChange={(value) => {
                if (!value) {
                  return;
                }

                const { label } = value;

                if ("EV" === label || "PHEV" === label) {
                  setFormData((prev) => ({
                    ...prev,
                    vehicleType: label,
                  }));
                }

                setFormData((prev) => {
                  const temp = { ...prev };
                  delete temp.ev;
                  return temp;
                });
              }}
            />

            <DropDown
              name="ev"
              value={formData?.ev?.id ?? null}
              virtualized
              options={(() => {
                if (!formData?.vehicleType) {
                  return [];
                }

                return formData.vehicleType === "EV" ? evList : phevList;
              })()}
              label={t("vehicle_make")}
              disabled={!formData?.vehicleType}
              onChange={(value) => {
                if (!formData?.vehicleType) {
                  return;
                }

                const options =
                  formData.vehicleType === "EV" ? evList : phevList;

                const newData = options.find((data) => data.id === value?.id);

                if (!newData) {
                  return;
                }

                setFormData((prev) => ({
                  ...prev,
                  ev: newData,
                }));
              }}
            />
          </div>
        </FieldContainer>

        <FieldContainer title={t("ice_vehicle")}>
          <DropDown
            name="ice"
            value={formData?.ice?.id ?? null}
            virtualized
            options={iceList}
            label={t("vehicle_make")}
            onChange={(value) => {
              const newData = iceList.find((data) => data.id === value?.id);

              if (!newData) {
                return;
              }

              setFormData((prev) => ({
                ...prev,
                ice: newData,
              }));
            }}
          />
        </FieldContainer>

        <div
          style={{
            display: "flex",
            width: "100%",
            gap: theme.spacing(2),
          }}
        >
          <FieldContainer title={t("province")}>
            <DropDown
              options={provinces.map((item) => ({
                id: item.id,
                label: `${item.code}`,
              }))}
              label={t("select_province")}
              value={formData?.province?.id ?? null}
              onChange={(value) => {
                const newData: ProvinceType | undefined = provinces.find(
                  (data) => data.code === value?.label,
                );
                setFormData((prev) => ({
                  ...prev,
                  province: newData,
                }));
              }}
            />
          </FieldContainer>

          <FieldContainer title={t("fuel_price_per_litre")}>
            <TextField
              name="fuelPrice"
              type="number"
              step={0.25}
              startAdornment={<>$</>}
              value={formData.fuelPrice}
              onChange={({ target }) =>
                handleFieldChange(target.name, target.value)
              }
            />
          </FieldContainer>
        </div>

        <FieldContainer
          title={t("annual_driving_distance")}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2),
          }}
        >
          <TextField
            name="annualDrivingDistance"
            type="number"
            step={1000}
            value={formData.annualDrivingDistance}
            onChange={({ target }) =>
              handleFieldChange(target.name, target.value)
            }
          />

          <Slider
            value={formData.annualDrivingDistance}
            onChange={(value) =>
              handleFieldChange("annualDrivingDistance", `${value}`)
            }
          />

          <Typography
            style={{ marginTop: theme.spacing(-2), fontStyle: "italic" }}
            align="center"
            variant="subtitle2"
          >
            {t("daily_average", {
              km: Math.trunc(formData.annualDrivingDistance / 365),
            })}
          </Typography>
        </FieldContainer>

        <div
          style={{
            display: "flex",
            width: "100%",
            gap: theme.spacing(2),
          }}
        >
          <FieldContainer title={t("utility_cost_per_kwh")}>
            <TextField
              name="utilityKWhRate"
              type="number"
              step={0.025}
              value={formData.utilityKWhRate}
              onChange={({ target }) =>
                handleFieldChange(target.name, target.value)
              }
            />
          </FieldContainer>

          <FieldContainer title={t("lifespan_years")}>
            <TextField
              name="lifespan"
              type="number"
              value={formData.lifespan}
              onChange={({ target }) =>
                handleFieldChange(target.name, target.value)
              }
            />
          </FieldContainer>
        </div>

        <AdvanceFeature
          advanceFeatureData={advanceFeatureData}
          setAdvanceFeatureData={setAdvanceFeatureData}
        />
      </div>

      {/*  row 2 */}
      <div
        style={{
          display: "flex",
          minWidth: "0",
          flexDirection: "column",
          alignItems: "center",
          flex: 9,
        }}
      >
        <FieldContainer noGap title="" style={{ width: "calc(100% + 40px)" }}>
          <FuelCostEmissionsChart
            data={[
              {
                type: `${t("ev")}`,
                fuelCost: calculationData.evAnnualFuelCostSavings,
                co2e: calculationData.ghgData.evCarbonFootprint,
                fuelCostColor: "#F9A21B",
              },
              {
                type: `${t("ice")}`,
                fuelCost: calculationData.iceAnnualFuelCostSavings,
                co2e: calculationData.ghgData.iceCarbonFootprint,
                fuelCostColor: "#0F1B2B",
              },
            ]}
            label={t("annual_fuel_cost_and_emissions")}
          />
          <RangeBarChart />
          <NetSavingsChart
            data={calculationData.evChartData.map((num, index) => ({
              year: `${index}`,
              netSaving: calculationData.iceChartData[index] - num || 0,
            }))}
            label={t("net_saving")}
            xAxisLabel={t("years")}
          />
        </FieldContainer>
      </div>
      {/*  row 3 */}
      <div
        style={{
          display: "flex",
          minWidth: "0",
          flexDirection: "column",
          flex: 16,
        }}
      >
        <FieldContainer
          title={t("lifetime_carbon_footprint")}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2),
          }}
        >
          {/* GHG calculator UI */}
          <GhgInfographic ghgData={calculationData.ghgData} />
        </FieldContainer>

        <FieldContainer noGap title={t("lifetime_fuel_cost")} marginLeft={20}>
          <LineChart
            width={500}
            xAxisLabel={t("years")}
            customLoadingMessage={t("select_some_data_to_display")}
            isLoading={!calculationData.isSet}
            data={[
              {
                label: t("ev"),
                data: calculationData.evLifetimeFuelCost,
                color: "#F9A21B",
              },
              {
                label: t("ice"),
                data: calculationData.iceLifetimeFuelCost,
                color: "#111F3B",
              },
            ]}
          />
        </FieldContainer>

        <FieldContainer noGap title={t("lifetime_total_cost")} marginLeft={20}>
          <LineChart
            width={500}
            xAxisLabel={t("years")}
            customLoadingMessage={t("select_some_data_to_display")}
            isLoading={!calculationData.isSet}
            data={[
              {
                label: `${t("ev")}`,
                data: calculationData.evChartData,
                color: "#F9A21B",
              },
              {
                label: `${t("ice")}`,
                data: calculationData.iceChartData,
                color: "#111F3B",
              },
            ]}
          />
        </FieldContainer>
      </div>
    </div>
  );
};

export default Calculator;
